/**
 * ISSUE-1188. The player page's Stats and Game log view model. The server
 * formats every value and computes every rank; this file only maps what it
 * sent into states.
 */
import type { typePlayerTabKey } from "../../routeState/playerSearch";

export type typePlayerStatFact = {
  statKey: string;
  label: string;
  displayValue: string;
  rankLabel: string | null;
};
export type typePlayerStatGroup = {
  groupKey: string;
  label: string;
  stats: typePlayerStatFact[];
};
export type typePlayerGameLogGame = {
  eventId: string;
  dateLabel: string;
  opponentLabel: string;
  resultCode: "W" | "L" | "T" | null;
  scoreLabel: string | null;
};
export type typePlayerGameLogCell = { key: string; display: string };
export type typePlayerGameLogRow = {
  eventId: string;
  cells: typePlayerGameLogCell[];
};
export type typePlayerGameLogTable = {
  key: string;
  label: string;
  columns: { key: string; label: string }[];
  rows: typePlayerGameLogRow[];
};
export type typePlayerStatsState =
  | { kind: "hidden" }
  | { kind: "notStarted" }
  | { kind: "ready"; groups: typePlayerStatGroup[] };
export type typePlayerGameLogState =
  | { kind: "hidden" }
  | { kind: "notStarted" }
  | {
      kind: "ready";
      games: typePlayerGameLogGame[];
      tables: typePlayerGameLogTable[];
    };
export type typePlayerSeasonView = {
  stats: typePlayerStatsState;
  gameLog: typePlayerGameLogState;
};

export const EMPTY_PLAYER_SEASON_VIEW: typePlayerSeasonView = {
  stats: { kind: "hidden" },
  gameLog: { kind: "hidden" },
};

export const PLAYER_GAME_LOG_MISSING_CELL = "-";

/** Structural: the generated response data. */
export type typePlayerSeasonStatsData = {
  hasLeagueStats: boolean;
  hasStats: boolean;
  groups: typePlayerStatGroup[];
};
export type typePlayerGameLogData = {
  hasTeamGames: boolean;
  hasGames: boolean;
  games: typePlayerGameLogGame[];
  tables: typePlayerGameLogTable[];
};

export function toPlayerStatsState(
  data: typePlayerSeasonStatsData | null | undefined,
): typePlayerStatsState {
  if (data === null || data === undefined || !data.hasLeagueStats) {
    return { kind: "hidden" };
  }
  if (!data.hasStats || data.groups.length === 0) {
    return { kind: "notStarted" };
  }
  return { kind: "ready", groups: data.groups };
}

export function toPlayerGameLogState(
  data: typePlayerGameLogData | null | undefined,
): typePlayerGameLogState {
  if (data === null || data === undefined || !data.hasTeamGames) {
    return { kind: "hidden" };
  }
  if (!data.hasGames || data.games.length === 0) {
    return { kind: "notStarted" };
  }
  return { kind: "ready", games: data.games, tables: data.tables };
}

/** Info always; Stats and Game log once their read says the tab has data or a not-started state. */
export function resolvePlayerOfferedTabs(
  season: typePlayerSeasonView,
): typePlayerTabKey[] {
  const tabs: typePlayerTabKey[] = ["info"];
  if (season.stats.kind !== "hidden") tabs.push("stats");
  if (season.gameLog.kind !== "hidden") tabs.push("gameLog");
  return tabs;
}

export function gameLogCellText(
  cells: readonly typePlayerGameLogCell[],
  key: string,
): string {
  return (
    cells.find((cell) => cell.key === key)?.display ??
    PLAYER_GAME_LOG_MISSING_CELL
  );
}

export function playerGameLogRowText(game: typePlayerGameLogGame): {
  primary: string;
  secondary: string | null;
} {
  const parts = [game.resultCode, game.scoreLabel].filter(
    (part): part is string => part !== null,
  );
  return {
    primary: `${game.dateLabel} ${game.opponentLabel}`,
    secondary: parts.length === 0 ? null : parts.join(" "),
  };
}
